import { z } from 'zod';
import { civilDateInTimeZone, dateString, dateStringParts } from './dateString.js';
import { isRentalOwnershipType } from './index.js';
export function budgetPropertyKindOf(type) {
    return isRentalOwnershipType(type) ? 'rental' : 'association';
}
export const BUDGET_CHARGE_CATEGORIES = {
    association: new Set(['Common Charges', 'Reserve Contributions']),
    rental: new Set(['Rental Income']),
};
export const FUND_CATEGORIES = new Set(['Special Assessments']);
export const BUSINESS_TIME_ZONE = 'America/New_York';
export function businessToday(now = new Date()) {
    return civilDateInTimeZone(now, BUSINESS_TIME_ZONE);
}
const ADOPTION_WINDOW_LAST_MONTH = 3;
export function plannableFiscalYears(today) {
    const { year, month } = dateStringParts(today);
    return month <= ADOPTION_WINDOW_LAST_MONTH ? [year, year + 1] : [year + 1];
}
export function fiscalYearsText(years) {
    return years.map((year) => `FY${year}`).join(' or ');
}
export function viewableFiscalYears(today) {
    const { year } = dateStringParts(today);
    return [year, year + 1];
}
export function planningAsOf(fiscalYear, today) {
    if (!viewableFiscalYears(today).includes(fiscalYear)) {
        throw new Error(`FY${fiscalYear} cannot be viewed on ${today}`);
    }
    const { year } = dateStringParts(today);
    return fiscalYear === year ? dateString(`${fiscalYear - 1}-12-31`) : today;
}
export const BUDGET_PLAN_HORIZON_YEARS = 5;
export const BUDGET_CHARGES_FALLBACK = {
    association: { category: 'Common Charges', subcategory: 'Residential Common Charges' },
    rental: { category: 'Rental Income', subcategory: 'Residential Rentals' },
};
export const budgetPlanStatusSchema = z.enum([
    'draft',
    'in_review',
    'committed',
    'rejected',
    'rolled_back',
]);
export const budgetApprovalPathSchema = z.enum([
    'governing_body',
    'owners',
    'governing_body_then_owners',
]);
export const budgetReserveApproachSchema = z.enum([
    'percent_of_income',
    'reserve_study',
    'charge_increase',
    'cover_expenses',
    'target_margin',
]);
export const BUDGET_APPROACHES = {
    association: ['percent_of_income', 'reserve_study', 'charge_increase'],
    rental: ['cover_expenses', 'charge_increase', 'target_margin'],
};
export function approachFitsKind(approach, kind) {
    return BUDGET_APPROACHES[kind].includes(approach);
}
export const LEGACY_HOLD_FLAT_BASIS = "Charges held at this year's level, decided under the earlier hold-income-flat rule.";
export const CHARGE_BASIS_MAX_LENGTH = 2000;
export const CHARGE_INCREASE_PERCENT_RANGE = { min: -50, max: 100 };
export const TARGET_MARGIN_PERCENT_RANGE = { min: 0, max: 60 };
export const budgetReserveSettingsSchema = z.discriminatedUnion('approach', [
    z.object({ approach: z.literal('percent_of_income') }),
    z.object({ approach: z.literal('reserve_study'), studyAmount: z.number().nonnegative() }),
    z.object({
        approach: z.literal('charge_increase'),
        percent: z
            .number()
            .min(CHARGE_INCREASE_PERCENT_RANGE.min)
            .max(CHARGE_INCREASE_PERCENT_RANGE.max),
        basis: z.string().max(CHARGE_BASIS_MAX_LENGTH),
    }),
    z.object({ approach: z.literal('cover_expenses') }),
    z.object({
        approach: z.literal('target_margin'),
        percent: z.number().min(TARGET_MARGIN_PERCENT_RANGE.min).max(TARGET_MARGIN_PERCENT_RANGE.max),
        basis: z.string().max(CHARGE_BASIS_MAX_LENGTH),
    }),
]);
export const DEFAULT_BUDGET_APPROACH = { association: { approach: 'percent_of_income' }, rental: { approach: 'cover_expenses' } };
const NO_FIGURES = {
    reserveStudyAmount: null,
    chargeIncreasePercent: null,
    chargeIncreaseBasis: null,
    targetMarginPercent: null,
    targetMarginBasis: null,
};
export function reserveSettingsOf(columns) {
    const approach = columns.reserveApproach;
    switch (approach) {
        case 'percent_of_income':
        case 'cover_expenses':
            return { approach };
        case 'reserve_study':
            if (columns.reserveStudyAmount === null) {
                throw new Error('A reserve study is stored with no amount');
            }
            return { approach, studyAmount: columns.reserveStudyAmount };
        case 'charge_increase':
            if (columns.chargeIncreasePercent === null || columns.chargeIncreaseBasis === null) {
                throw new Error('A charge increase is stored with no percent or basis');
            }
            return {
                approach,
                percent: columns.chargeIncreasePercent,
                basis: columns.chargeIncreaseBasis,
            };
        case 'target_margin':
            if (columns.targetMarginPercent === null || columns.targetMarginBasis === null) {
                throw new Error('A target margin is stored with no percent or basis');
            }
            return {
                approach,
                percent: columns.targetMarginPercent,
                basis: columns.targetMarginBasis,
            };
        default: {
            const _exhaustive = approach;
            throw new Error(`Unhandled reserve approach: ${String(_exhaustive)}`);
        }
    }
}
export function reserveColumnsOf(settings) {
    switch (settings.approach) {
        case 'percent_of_income':
        case 'cover_expenses':
            return { reserveApproach: settings.approach, ...NO_FIGURES };
        case 'reserve_study':
            return {
                reserveApproach: 'reserve_study',
                ...NO_FIGURES,
                reserveStudyAmount: settings.studyAmount,
            };
        case 'charge_increase':
            return {
                reserveApproach: 'charge_increase',
                ...NO_FIGURES,
                chargeIncreasePercent: settings.percent,
                chargeIncreaseBasis: settings.basis,
            };
        case 'target_margin':
            return {
                reserveApproach: 'target_margin',
                ...NO_FIGURES,
                targetMarginPercent: settings.percent,
                targetMarginBasis: settings.basis,
            };
        default: {
            const _exhaustive = settings;
            throw new Error(`Unhandled reserve approach: ${JSON.stringify(_exhaustive)}`);
        }
    }
}
export const budgetLineOriginSchema = z.enum(['edit', 'finding']);
export const budgetFindingDecisionSchema = z.enum(['accept', 'defer', 'not_applicable']);
export const budgetProposalStatusSchema = z.enum(['pending', 'approved', 'rejected']);
export const budgetApprovalBodySchema = z.enum(['governing_body', 'owners']);
export const budgetApprovalDecisionSchema = z.enum(['approved', 'rejected']);
export function requiredApprovalSteps(path) {
    switch (path) {
        case 'governing_body':
            return ['governing_body'];
        case 'owners':
            return ['owners'];
        case 'governing_body_then_owners':
            return ['governing_body', 'owners'];
        default: {
            const _exhaustive = path;
            throw new Error(`Unhandled approval path: ${String(_exhaustive)}`);
        }
    }
}
const SAMPLE_FINDING_HOSTS = new Set(['localhost', '127.0.0.1', 'uat.theteoai.com']);
export function sampleFindingsAllowed(hostname) {
    if (hostname === undefined)
        return false;
    return SAMPLE_FINDING_HOSTS.has(hostname) || hostname.endsWith('.localhost');
}
//# sourceMappingURL=budgetPlanning.js.map